import { diff } from "deep-diff";
import { ContactReportRole, Prisma } from "encamp-prisma/generated/browser";
import { sortBy } from "lodash-es";

import {
  AddressInput,
  CreateFacilityContactInput,
  CreatePersonInput,
  FacilityContact,
  Person,
  Phone,
  PhoneInput,
  PhoneType,
  ReportType,
  UpdateFacilityContactInput,
  UpdatePersonInput,
} from "../generated-graphql";
import { TierIIContactType } from "../types";
import { isJsonObject } from "../utils/isJsonObject";

export const TIER_II_CONTACT_TYPE_REPORT_ROLE_MAP: {
  [key in TierIIContactType]: ContactReportRole;
} = {
  "Chemical Carrier": ContactReportRole.EPCRA_CHEMICAL_CARRIER,
  "Emergency Contacts": ContactReportRole.EPCRA_EMERGENCY_CONTACT,
  "Emergency Coordinator": ContactReportRole.EPCRA_EMERGENCY_COORDINATOR,
  "Owner / Operator": ContactReportRole.EPCRA_OWNER_OPERATOR,
  "Tier II Contact": ContactReportRole.EPCRA_TIER_II_CONTACT,
  "Billing Contact": ContactReportRole.EPCRA_BILLING,
  "Emergency Equipment Contact": ContactReportRole.EPCRA_EMERGENCY_EQUIPMENT_CONTACT,
  Other: ContactReportRole.GENERAL,
};

export const epcraReportingRoles: { [key: string]: TierIIContactType } = Object.fromEntries(
  Object.entries(TIER_II_CONTACT_TYPE_REPORT_ROLE_MAP).map(([key, value]) => [value, key])
) as { [key: string]: TierIIContactType };

export const reportTypes: { [key: string]: string } = {
  [ReportType.EPCRA]: ReportType.EPCRA,
  [ReportType.RCRA]: ReportType.RCRA,
};

/**
 * Contact roles that are for internal tracking only and should NOT appear on
 * TierII reports (snapshots, PDFs, or submitted data).
 *
 * These roles can still be assigned to contacts and displayed in the facility
 * contacts management UI, but they are filtered out when:
 * - Loading contacts for report snapshots
 * - Displaying contacts in the report UI
 * - Generating report PDFs
 * - Submitting reports to agencies (T2Submit, EPlan, etc.)
 */
export const INTERNAL_ONLY_CONTACT_ROLES: ContactReportRole[] = [
  ContactReportRole.EPCRA_REGIONAL_ENVIRONMENTAL_CONTACT,
];

export function getPhoneNumberFromJsonArray(
  phones: Prisma.JsonArray,
  reportType: "EPCRA" | "RCRA",
  phoneType: PhoneType
): string | undefined {
  if (!Array.isArray(phones)) throw new Error("phones is not array like");

  // find the phone that
  // 1. has the phoneType we want
  // 2. and if phone.reportTypes is an array, has the reportType we want
  const phone = phones
    .filter(isJsonObject)
    .find(
      (phone) =>
        phone?.type === phoneType &&
        (!Array.isArray(phone?.reportTypes) || phone?.reportTypes?.includes(reportType))
    );

  if (!phone?.number || typeof phone.number !== "string") return;

  return phone.number;
}

/**
 * Gets all phone numbers of a specific type from a JSON array.
 * Unlike getPhoneNumberFromJsonArray, this returns all matching phone numbers,
 * not just the first one.
 *
 * @param phones - JSON array of phone objects
 * @param reportType - The report type to filter by (EPCRA or RCRA)
 * @param phoneType - The phone type to filter by
 * @returns Array of phone number strings matching the criteria
 */
export function getPhoneNumbersFromJsonArray(
  phones: Prisma.JsonArray,
  reportType: "EPCRA" | "RCRA",
  phoneType: PhoneType
): string[] {
  if (!Array.isArray(phones)) throw new Error("phones is not array like");

  // filter all phones that
  // 1. have the phoneType we want
  // 2. and if phone.reportTypes is an array, have the reportType we want
  return phones
    .filter(isJsonObject)
    .filter(
      (phone) =>
        phone?.type === phoneType &&
        (!Array.isArray(phone?.reportTypes) || phone?.reportTypes?.includes(reportType))
    )
    .map((phone) => phone?.number)
    .filter((number): number is string => typeof number === "string");
}

/**
 * @deprecated This is really just used in legacy callsites. I could modify it to be more flexible, but it's not a priority.
 */
export function getPhoneNumberForReport(
  phones: Phone[] | undefined | null,
  reportType: ReportType,
  phoneType: PhoneType
) {
  return phones?.find((p) => p.reportTypes?.includes(reportType) && p.type === phoneType)?.number;
}

export function stripFacilityContactToUpdateInput<
  T extends UpdateFacilityContactInput | CreateFacilityContactInput,
>(facilityContact: FacilityContact): T {
  // Strip out everything that is not in the UpdateFacilityContactInput type
  const { __typename, updatedBy, createdAt, updatedAt, person, facility, ...updateInput } =
    facilityContact;

  return { ...updateInput } as T;
}

export function stripPersonToInput<T extends UpdatePersonInput | CreatePersonInput>(
  person: Person
): T {
  const { __typename, updatedBy, createdAt, updatedAt, facilities, userTenant, ...input } = person;

  let addressInput: AddressInput | undefined;
  if (input.address) {
    const { __typename: addressTypename, ...strippedAddress } = input.address;
    addressInput = strippedAddress;
  }

  let phonesInput: PhoneInput[] = [];
  if (input.phones) {
    phonesInput = input.phones.map((phone) => {
      const { __typename, ...strippedPhone } = phone;
      return strippedPhone as PhoneInput;
    });
  }

  return { ...input, address: addressInput, phones: phonesInput } as T;
}

/**
 * Tells whether or not two Person objects have identical data and strips Person
 * objects down to an update or create input to be used for an AppSync mutation
 * @param a First Person to compare
 * @param b Second Person to compare
 * @returns {result: boolean, personInputA: T, personInputB: T} where T is of UpdatePersonInput or CreatePersonInput type
 */
export function arePeopleEqual<T extends UpdatePersonInput | CreatePersonInput>(
  a: Person,
  b: Person
): { result: boolean; personInputA: T; personInputB: T } {
  const personInputA = stripPersonToInput<T>(a);
  const personInputB = stripPersonToInput<T>(b);
  // Sort phone numbers in both objects to help prevent false negatives
  const phoneSortBys = [
    (p: PhoneInput) => {
      return p.number;
    },
    (p: PhoneInput) => {
      return p.type;
    },
  ];

  const diffs =
    diff(
      {
        ...personInputA,
        id: "",
        phones: sortBy(personInputA.phones, phoneSortBys),
      },
      {
        ...personInputB,
        id: "",
        phones: sortBy(personInputB.phones, phoneSortBys),
      }
    ) ?? [];

  return { result: diffs.length === 0, personInputA, personInputB };
}
